import { MathUtils, Matrix4, Quaternion, Vector3 } from 'three';
import type { Object3D, PerspectiveCamera } from 'three';
import { Spring } from '../boxer/spring';
import { fieldOfView } from '../render/fieldOfView';

/** A camera move of its own, in the world, over so many real seconds. */
interface Shot {
  readonly seconds: number;
  /** Where the camera is, and what it looks at, this far into the shot (0..1). */
  pose(progress: number, position: Vector3, target: Vector3): void;
  /** How much of the shot shows, against the player's own eyes, this far in (0..1). */
  weight(progress: number): number;
  /** Degrees added to the field of view. */
  readonly widen: number;
}

const eyes = new Vector3();
const eyesQuaternion = new Quaternion();
const shotPosition = new Vector3();
const shotTarget = new Vector3();
const shotQuaternion = new Quaternion();
const look = new Matrix4();
const up = new Vector3(0, 1, 0);
const parentQuaternion = new Quaternion();
const ahead = new Vector3();

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/**
 * The camera's director: most of the time the camera is the player's eyes, and the director only punches the lens in
 * on a big hit. For a moment of its own, a crane down into the player's eyes before the first round or a slow orbit
 * round a knockout, it takes the camera and blends back to the eyes when done. It runs after the player's view has
 * placed the camera for the frame, and hides the player's arms while it is away from his eyes.
 */
export class CameraDirector {
  private shot: Shot | null = null;
  private elapsed = 0;
  private readonly punch = new Spring(0, 180, 20);

  constructor(private readonly camera: PerspectiveCamera, private readonly arms: readonly Object3D[]) {}

  /** True while a shot of its own is on. */
  get filming(): boolean {
    return this.shot !== null;
  }

  /** Down from high over the ring into the player's eyes, looking at the opponent all the way. */
  craneIn(opponentFace: Vector3, seconds: number): void {
    const start = new Vector3(1.9, 3.1, 3.4);
    const face = opponentFace.clone();
    this.begin({
      seconds,
      widen: 8,
      pose: (t, position, target) => {
        const e = easeInOutCubic(t);
        // Along a curve that swings out to the side and comes in level, so the ring turns under the camera.
        position.lerpVectors(start, eyes, e);
        position.x += Math.sin(t * Math.PI) * 0.6;
        ahead.set(0, 0, -1).applyQuaternion(eyesQuaternion).add(eyes);
        target.copy(face).lerp(ahead, e * e);
      },
      weight: (t) => (t < 1 ? 1 : 0),
    });
  }

  /** Out of the player's eyes and slowly round him as he goes down, low over the canvas. */
  knockout(head: Object3D, standsAt: Vector3, seconds: number): void {
    const centre = standsAt.clone();
    this.begin({
      seconds,
      widen: -6,
      pose: (t, position, target) => {
        const angle = MathUtils.lerp(0.35, 1.45, t);
        const radius = MathUtils.lerp(2.3, 1.7, t);
        position.set(centre.x + Math.sin(angle) * radius, MathUtils.lerp(1.1, 0.55, t), centre.z + Math.cos(angle) * radius * 0.8 - 0.3);
        head.getWorldPosition(target);
        target.y = Math.max(target.y, 0.35);
      },
      // In quickly, out gently at the end, back into the player's eyes for the result.
      weight: (t) => Math.min(1, t / 0.08, (1 - t) / 0.15),
    });
  }

  /** A big hit: the lens punches in, by about six degrees at full strength, and eases back. */
  punchIn(strength: number): void {
    this.punch.kick(-180 * strength);
  }

  /** Every frame, after the player's view has placed the camera. */
  update(real: number): void {
    const camera = this.camera;
    const parent = camera.parent!;
    parent.updateMatrixWorld(true);
    camera.updateMatrixWorld(true);
    let widen = 0;
    let weight = 0;
    if (this.shot) {
      this.elapsed += real;
      const t = Math.min(1, this.elapsed / this.shot.seconds);
      camera.getWorldPosition(eyes);
      camera.getWorldQuaternion(eyesQuaternion);
      weight = this.shot.weight(t);
      this.shot.pose(t, shotPosition, shotTarget);
      widen = this.shot.widen * weight;
      if (weight > 0) {
        // Cameras look along their -Z: a look-at from the camera to the target.
        shotQuaternion.setFromRotationMatrix(look.lookAt(shotPosition, shotTarget, up));
        eyes.lerp(shotPosition, weight);
        eyesQuaternion.slerp(shotQuaternion, weight);
        camera.position.copy(parent.worldToLocal(eyes));
        parent.getWorldQuaternion(parentQuaternion);
        camera.quaternion.copy(parentQuaternion.invert().multiply(eyesQuaternion));
      }
      if (t >= 1) this.shot = null;
    }
    for (const arm of this.arms) arm.visible = weight < 0.5;
    camera.fov = fieldOfView(camera.aspect) + widen + this.punch.update(0, real);
    camera.updateProjectionMatrix();
  }

  private begin(shot: Shot): void {
    this.shot = shot;
    this.elapsed = 0;
  }
}
